import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useCart } from 'services/cart/CartContext';
import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import { auth } from 'services/firebase/client';
import { analyticsService } from 'services/analytics/analyticsService';
import { useShippingAbandonTracking } from 'hooks/useShippingAbandonTracking';
import { getMelhorEnvioQuote } from 'services/shippingService';
import {
  getDestinationCity,
  getDestinationPostalCode,
  getDestinationUf,
  isAraraquaraAddress,
} from 'utils/shippingPersonalData';
import {
  formatCurrencyBRL,
  roundCurrency,
  sumCurrency,
} from 'utils/currency';
import styles from './styles';

interface ShippingOption {
  id: string;
  description: string;
  price: number;
  deliveryTime: string;
}

const pickupOption: ShippingOption = {
  id: 'retira',
  description: 'Retira na Cervejaria (em Araraquara-SP)',
  price: 0.0,
  deliveryTime: 'De quarta à sábado - 18h às 23h',
};

const BeerStoreShipping: React.FC = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const { personalData, hasActiveSubscription, loading: personalDataLoading } =
    useSubscription();
  const [selectedShipping, setSelectedShipping] = useState('');
  const [shippingPrice, setShippingPrice] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  const subtotalValue = hasActiveSubscription
    ? cart.subscriberTotalValue
    : cart.totalValue;

  const cartLabel = `Loja (${cart.totalItems} itens)`;
  const destinationPostalCode = getDestinationPostalCode(personalData);
  const destinationCity = getDestinationCity(personalData);
  const destinationUf = getDestinationUf(personalData);
  const showPickupOption = isAraraquaraAddress(personalData);

  const [deliveryQuote, setDeliveryQuote] = useState<{
    price: number;
    deliveryTime: string;
    description: string;
  } | null>(null);

  const { markAdvanceClicked } = useShippingAbandonTracking(
    'beer_store_shipping',
    deliveryQuote?.price ?? null,
    cartLabel,
  );

  useEffect(() => {
    if (cart.items.length === 0) {
      navigate('/beer-store');
      return;
    }

    if (
      !personalDataLoading &&
      (!personalData || !personalData.address?.street)
    ) {
      navigate('/subscriptionform');
    }
  }, [cart.items.length, personalData, personalDataLoading, navigate]);

  useEffect(() => {
    if (personalDataLoading) {
      return;
    }

    if (destinationPostalCode.length !== 8 || cart.totalItems <= 0) {
      setDeliveryQuote(null);
      setQuoteError(
        destinationPostalCode.length !== 8
          ? 'CEP inválido para cálculo de frete.'
          : '',
      );
      return;
    }

    let cancelled = false;

    const fetchQuote = async () => {
      setIsLoadingQuote(true);
      setQuoteError('');

      analyticsService.trackShippingQuoteRequested({
        source: 'beer_store_shipping',
        cepPrefix: destinationPostalCode,
        subscriptionPlan: cartLabel,
      });

      try {
        const quote = await getMelhorEnvioQuote({
          destinationPostalCode,
          destinationCity,
          destinationUf,
          bottleCount: cart.totalItems,
          insuranceValue: Math.max(subtotalValue, 0),
        });

        if (cancelled) {
          return;
        }

        setDeliveryQuote({
          price: quote.price,
          deliveryTime: quote.deliveryTimeLabel,
          description: 'Frete',
        });

        analyticsService.trackShippingQuoteSuccess({
          source: 'beer_store_shipping',
          cepPrefix: destinationPostalCode,
          shippingPrice: quote.price,
          subscriptionPlan: cartLabel,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Erro ao cotar frete:', error);
        setDeliveryQuote(null);

        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Não foi possível calcular o frete.';

        analyticsService.trackShippingQuoteError({
          source: 'beer_store_shipping',
          cepPrefix: destinationPostalCode,
          subscriptionPlan: cartLabel,
          errorMessage,
        });

        setQuoteError(errorMessage);
      } finally {
        if (!cancelled) {
          setIsLoadingQuote(false);
        }
      }
    };

    void fetchQuote();

    return () => {
      cancelled = true;
    };
  }, [
    cart.totalItems,
    cartLabel,
    destinationCity,
    destinationPostalCode,
    destinationUf,
    personalDataLoading,
    subtotalValue,
  ]);

  const applyShippingSelection = useCallback((option: ShippingOption) => {
    const roundedPrice = roundCurrency(option.price);
    setSelectedShipping(option.id);
    setShippingPrice(roundedPrice);
  }, []);

  const allShippingOptions = useMemo(() => {
    const deliveryOptions: ShippingOption[] = deliveryQuote
      ? [
          {
            id: 'economico',
            description: deliveryQuote.description,
            price: deliveryQuote.price,
            deliveryTime: deliveryQuote.deliveryTime,
          },
        ]
      : [];

    return showPickupOption
      ? [...deliveryOptions, pickupOption]
      : deliveryOptions;
  }, [deliveryQuote, showPickupOption]);

  useEffect(() => {
    if (allShippingOptions.length === 0) {
      setSelectedShipping('');
      setShippingPrice(0);
      return;
    }

    const matched = allShippingOptions.find(o => o.id === selectedShipping);

    if (!matched) {
      applyShippingSelection(allShippingOptions[0]);
      return;
    }

    if (shippingPrice !== matched.price) {
      applyShippingSelection(matched);
    }
  }, [
    allShippingOptions,
    applyShippingSelection,
    selectedShipping,
    shippingPrice,
  ]);

  const handleShippingChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const selectedOption = allShippingOptions.find(
        option => option.id === event.target.value,
      );

      if (selectedOption) {
        applyShippingSelection(selectedOption);
      }
    },
    [allShippingOptions, applyShippingSelection],
  );

  const validateForm = (): boolean => {
    if (!selectedShipping) {
      alert('Selecione uma opção de frete.');
      return false;
    }

    if (!personalData || !personalData.address?.street) {
      alert('Dados de endereço não encontrados. Redirecionando...');
      navigate('/subscriptionform');
      return false;
    }

    return true;
  };

  const handleProceedToPayment = async () => {
    if (!validateForm()) return;

    setIsProcessing(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        alert('Você precisa estar logado para continuar.');
        navigate('/login');
        return;
      }

      markAdvanceClicked();
      analyticsService.trackShippingAdvanceClick({
        source: 'beer_store_shipping',
        isAuthenticated: true,
        hadShippingQuote: deliveryQuote !== null,
        shippingPrice,
        subscriptionPlan: cartLabel,
        redirectTo: '/beer-store-payment',
      });

      const orderId = uuidv4();
      const selectedOption = allShippingOptions.find(
        opt => opt.id === selectedShipping,
      );

      const orderData = {
        orderId,
        cart,
        shipping: {
          id: selectedShipping,
          price: shippingPrice,
          description: selectedOption?.description,
          deliveryTime: selectedOption?.deliveryTime,
        },
        address: personalData.address,
        userId: user.uid,
        userEmail: user.email,
      };

      localStorage.setItem('beerStoreOrderData', JSON.stringify(orderData));

      navigate('/beer-store-payment');
    } catch (error) {
      console.error('Erro ao processar pedido:', error);
      alert('Erro ao processar pedido. Tente novamente.');
    } finally {
      setIsProcessing(false);
    }
  };

  const total = sumCurrency(subtotalValue, shippingPrice);
  const displayCep = personalData?.cep || personalData?.address?.zip || '';

  if (personalDataLoading) {
    return (
      <Box
        sx={{
          ...styles.container,
          display: 'flex',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h4" sx={styles.title}>
          Finalizar Pedido
        </Typography>
        <Typography variant="subtitle1" sx={styles.subtitle}>
          Confirme seu endereço e escolha a opção de frete
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            ENDEREÇO SELECIONADO
          </Typography>
          <Card sx={styles.card}>
            <CardContent>
              <Typography variant="body1" sx={styles.addressText}>
                {personalData?.address.street}, {personalData?.address.number}
              </Typography>
              <Typography variant="body1" sx={styles.addressText}>
                {destinationCity} - {destinationUf}
              </Typography>
              <Typography variant="body2" sx={styles.addressDetails}>
                {personalData?.address.neighborhood} - {displayCep}
              </Typography>
              <Typography variant="body2" sx={styles.addressDetails}>
                {personalData?.fullName}
              </Typography>
            </CardContent>
          </Card>

          <Typography variant="h6" sx={styles.sectionTitle}>
            ESCOLHA A OPÇÃO DE FRETE
          </Typography>
          {isLoadingQuote && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Calculando frete...
              </Typography>
            </Box>
          )}
          {quoteError && !isLoadingQuote && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {quoteError}
            </Typography>
          )}
          <RadioGroup
            name="shipping"
            value={selectedShipping}
            onChange={handleShippingChange}
          >
            {allShippingOptions.map(option => (
              <Card key={option.id} sx={styles.shippingCard}>
                <CardContent sx={styles.shippingCardContent}>
                  <FormControlLabel
                    value={option.id}
                    control={<Radio sx={styles.radio} />}
                    label={
                      <Box>
                        <Typography variant="body1" sx={styles.shippingTitle}>
                          {option.description}
                        </Typography>
                        <Typography variant="body2" sx={styles.shippingDetails}>
                          {option.deliveryTime}
                        </Typography>
                      </Box>
                    }
                  />
                  <Typography variant="h6" sx={styles.shippingPrice}>
                    {option.price === 0
                      ? 'GRÁTIS'
                      : `R$ ${formatCurrencyBRL(option.price)}`}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </RadioGroup>
          <Box sx={styles.shippingInfo}>
            <Typography variant="caption" sx={styles.disclaimer}>
              * O prazo de entrega passa a valer a partir do despacho do pedido
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={5}>
          <Typography variant="h6" sx={styles.sectionTitle}>
            RESUMO DO PEDIDO
          </Typography>
          <Card sx={styles.summaryCard}>
            <CardContent>
              <Box sx={styles.cartItems}>
                {cart.items.map(item => (
                  <Box key={item.id} sx={styles.cartItem}>
                    <Box sx={styles.itemInfo}>
                      <Typography variant="body2" sx={styles.itemName}>
                        {item.beer.name}
                      </Typography>
                      <Typography variant="caption" sx={styles.itemDetails}>
                        {item.beer.brewery} • {item.quantity}x
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={styles.itemPrice}>
                      R${' '}
                      {(
                        (hasActiveSubscription
                          ? item.beer.subscriberPrice
                          : item.beer.price) * item.quantity
                      ).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider sx={styles.summaryDivider} />

              <Box sx={styles.summaryRow}>
                <Typography variant="body2">
                  Subtotal ({cart.totalItems} itens)
                </Typography>
                <Typography variant="body2">
                  R$ {formatCurrencyBRL(subtotalValue)}
                </Typography>
              </Box>

              <Box sx={styles.summaryRow}>
                <Typography variant="body2">Frete</Typography>
                <Typography variant="body2">
                  {shippingPrice === 0
                    ? 'GRÁTIS'
                    : `R$ ${formatCurrencyBRL(shippingPrice)}`}
                </Typography>
              </Box>

              <Divider sx={styles.summaryDivider} />

              {hasActiveSubscription && (
                <Box sx={styles.subscriberSection}>
                  <Typography variant="caption" sx={styles.subscriberLabel}>
                    ✓ Preços de assinante aplicados
                  </Typography>
                </Box>
              )}

              <Box sx={styles.totalRow}>
                <Typography variant="h6" sx={styles.totalLabel}>
                  TOTAL
                </Typography>
                <Typography variant="h6" sx={styles.totalValue}>
                  R$ {formatCurrencyBRL(total)}
                </Typography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleProceedToPayment}
                disabled={
                  isProcessing ||
                  isLoadingQuote ||
                  !selectedShipping ||
                  !!quoteError
                }
                startIcon={
                  isProcessing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : undefined
                }
                sx={styles.checkoutButton}
              >
                {isProcessing ? 'Processando...' : 'CONTINUAR PARA PAGAMENTO'}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BeerStoreShipping;
